import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { BalanceWithDeltaCell } from '../cells';
import { currency } from '../formatters';
import type { ColumnGroup } from '../types';
import type { StrategyPresentation } from './types';

type CmTwoLikeDeltas = {
  baseBenefitPaid?: number;
  riderBenefitPaid?: number;
  epLumpSumPaid?: number;
  epLumpSumBasePaid?: number;
  epLumpSumDays?: number;
  epLumpSumDailyRate?: number;
  indemnitySurplusAccrued?: number;
  indemnitySurplusApplied?: number;
};
type CmTwoLikeState = {
  indemnitySurplus?: number;
};

const surplusOf = (s: MonthlyState): number =>
  (s.strategyState as CmTwoLikeState).indemnitySurplus ?? 0;

function surplusTooltipFor(month: MonthlyEntry, openState: MonthlyState) {
  const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
  const accrued = sd.indemnitySurplusAccrued ?? 0;
  const applied = sd.indemnitySurplusApplied ?? 0;
  const open = surplusOf(openState);
  const close = surplusOf(month.state);
  if (open === 0 && close === 0 && accrued === 0 && applied === 0) return null;
  return (
    <BalanceBreakdownTooltip
      title="Indemnity surplus"
      description="Indemnity policies pay a fixed monthly benefit regardless of actual cost. Months where the benefit exceeds cost accrue the excess as a surplus reserve, which is drawn down later to cover months where cost exceeds the benefit."
      sections={[
        {
          opening: open,
          closing: close,
          lines: [
            { label: 'Accrued (benefit > cost)', value: accrued, kind: 'add' },
            { label: 'Applied (cost > benefit)', value: applied, kind: 'subtract' },
          ],
        },
      ]}
    />
  );
}

function surplusColumnGroup(): ColumnGroup {
  return {
    columns: [
      {
        key: 'surplus',
        label: 'Surplus balance',
        tooltip:
          'End-of-month surplus balance, with this month’s change beneath (+ accrued / − applied).',
        render: ({ month, openState }) => (
          <BalanceWithDeltaCell
            opening={surplusOf(openState)}
            closing={surplusOf(month.state)}
            tooltipContent={surplusTooltipFor(month, openState) ?? undefined}
          />
        ),
        renderAggregate: ({ openStateAtStart, closeStateAtEnd, kind }) => (
          <BalanceWithDeltaCell
            opening={kind === 'total' ? surplusOf(closeStateAtEnd) : surplusOf(openStateAtStart)}
            closing={surplusOf(closeStateAtEnd)}
          />
        ),
      },
    ],
  };
}

function EpLumpTooltipContent({
  days,
  dailyRate,
  total,
}: {
  days: number;
  dailyRate: number;
  total: number;
}) {
  return (
    <TooltipContent className="text-xs">
      <div className="mb-1 font-semibold">Waiting period reimbursement</div>
      <div className="mb-1 text-[10px] text-gray-300">
        Once the waiting period is satisfied, days served during it are paid
        back as a lump sum at the inflated daily benefit on the satisfaction
        date.
      </div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Days served</span>
        <span className="font-semibold tabular-nums">{days}</span>
        <span className="text-gray-300">Inflated daily rate</span>
        <span className="font-semibold tabular-nums">{currency(dailyRate)}/day</span>
        <span className="text-gray-300">Lump sum</span>
        <span className="font-semibold tabular-nums">
          {days} × {currency(dailyRate)} = {currency(total)}
        </span>
      </div>
    </TooltipContent>
  );
}

export const cmTwoPresentation: StrategyPresentation = {
  // Surplus only makes sense on indemnity products — reimbursement products
  // can't accrue a surplus reserve. Gated here so a future reimbursement
  // hybrid routed through this presentation would correctly suppress it.
  extraPayoutColumnGroups: ({ terms }) =>
    terms.isIndemnity ? [surplusColumnGroup()] : [],

  poolCellTooltip: ({ month, openState }) => {
    const open = openState.pools.baseBenefitPool;
    const close = month.state.pools.baseBenefitPool;
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const baseDrawn = sd.baseBenefitPaid ?? 0;
    const lumpBase = sd.epLumpSumBasePaid ?? 0;
    if (open === 0 && close === 0 && baseDrawn === 0 && lumpBase === 0) return null;
    return (
      <BalanceBreakdownTooltip
        title="LTC pool"
        description="CareMatters II's base pool. Only the base monthly benefit (and the base portion of any EP retroactive lump) draws down the pool — the inflation rider is paid on top and does not deplete it."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [
              { label: 'Base benefit drawn', value: baseDrawn, kind: 'subtract' },
              { label: 'EP retroactive (base portion)', value: lumpBase, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const baseDrawn = sd.baseBenefitPaid ?? 0;
    const lumpBase = sd.epLumpSumBasePaid ?? 0;
    if (open === 0 && close === 0 && baseDrawn === 0 && lumpBase === 0) return null;
    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="Flat Face Amount, depleted by the base benefit payout (and the base portion of any EP retroactive lump). The inflation rider is paid on top and does not deplete the death benefit. Floors at the minimum guaranteed amount."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [
              { label: 'Base benefit drawn', value: baseDrawn, kind: 'subtract' },
              { label: 'EP retroactive (base portion)', value: lumpBase, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month }) => {
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const basePay = sd.baseBenefitPaid ?? 0;
    const riderPay = sd.riderBenefitPaid ?? 0;
    const total = basePay + riderPay;
    const cost = month.facts.effectiveCareCost;
    const indemnityPayout = month.deltas.payout.indemnityPayout;
    const surplusApplied = sd.indemnitySurplusApplied ?? 0;
    const surplusAccrued = sd.indemnitySurplusAccrued ?? 0;
    if (total === 0 && (sd.epLumpSumPaid ?? 0) === 0) return null;

    const rule =
      indemnityPayout >= cost
        ? 'CareMatters II pays a fixed monthly indemnity benefit regardless of actual cost. This month\'s benefit covers the full cost; the excess accrues as a surplus reserve.'
        : 'CareMatters II pays a fixed monthly indemnity benefit. The benefit is below this month\'s cost — the surplus reserve (if any) covers part of the gap; the remainder is out-of-pocket.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — indemnity</div>
        <div className="mb-1 text-[10px] text-gray-300">{rule}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Eligible care cost</span>
          <span className="font-semibold tabular-nums">{currency(cost)}</span>
          <span className="text-gray-300">Base benefit</span>
          <span className="font-semibold tabular-nums">{currency(basePay)}</span>
          {riderPay > 0 && (
            <>
              <span className="text-gray-300">+ Inflation rider</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                +{currency(riderPay)}
              </span>
            </>
          )}
          <span className="text-gray-300">Total paid</span>
          <span className="font-semibold tabular-nums">{currency(total)}</span>
          {surplusAccrued > 0 && (
            <>
              <span className="text-gray-300">Surplus accrued</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                +{currency(surplusAccrued)}
              </span>
            </>
          )}
          {surplusApplied > 0 && (
            <>
              <span className="text-gray-300">Surplus applied</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                +{currency(surplusApplied)}
              </span>
            </>
          )}
        </div>
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const surplusApplied = sd.indemnitySurplusApplied ?? 0;
    const epLump = sd.epLumpSumPaid ?? 0;
    const covered = month.deltas.payout.ltcCoveragePaid;
    const withSurplus = month.deltas.payout.ltcPayoutToCost;

    if (surplusApplied === 0 && epLump === 0) return null;

    return (
      <>
        {surplusApplied > 0 && withSurplus !== covered && (
          <div className="text-[10px] text-emerald-700 inline-block">
            +{currency(surplusApplied)} surplus
          </div>
        )}
        {epLump > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-[10px] text-amber-700 cursor-help border-b border-dotted border-amber-300 inline-block">
                +{currency(epLump)} WP reimbursement
              </div>
            </TooltipTrigger>
            <EpLumpTooltipContent
              days={sd.epLumpSumDays ?? 0}
              dailyRate={sd.epLumpSumDailyRate ?? 0}
              total={epLump}
            />
          </Tooltip>
        )}
      </>
    );
  },

  copy: {
    poolTooltip:
      'End-of-month LTC pool balance, with this month’s change beneath. Only the base benefit payout draws down the pool — the inflation rider is paid on top and does not deplete it.',
    payoutColumnTooltip:
      'Indemnity payout this month (base + rider, plus any waiting-period retroactive lump), with cumulative dollars paid by the policy through this month in parentheses.',
    benefitInflationCompoundLabel: 'Compound rider growth',
    benefitInflationCapNote: capYear => `Rider capped at year ${capYear}`,
  },
};
